import { Alert, Box, Button, Divider, Stack } from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { BulkOverrideConfirm, FormRowSwitch, FormSection, MyModal, NumberStepper } from "components";
import { ENDPOINTS, filterModelsForUI, MODEL_LABELS, MODELS, UI_MODELS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ModelTypes, SiteModel } from "types";

const sensitivityFields = [
  { key: "overallST" as const, labelKey: "Overall Sensitivity" },
  { key: "zoneST" as const, labelKey: "Zone Sensitivity" },
  { key: "gestureST" as const, labelKey: "Gesture Sensitivity" },
  { key: "weaponST" as const, labelKey: "Weapon Sensitivity" },
  { key: "fightST" as const, labelKey: "Fight Sensitivity" },
  { key: "passedOutST" as const, labelKey: "Passed Out Sensitivity" },
  { key: "abusiveST" as const, labelKey: "Abuse Detection Sensitivity" }
];

type SensitivityKey = (typeof sensitivityFields)[number]["key"];

const defaultSensitivity: Record<SensitivityKey, number> = {
  overallST: 5,
  zoneST: 5,
  gestureST: 5,
  weaponST: 5,
  fightST: 5,
  passedOutST: 5,
  abusiveST: 5
};

function settingsFingerprint(site: SiteModel) {
  const models = filterModelsForUI(site.enabledModels as ModelTypes[]).sort().join(",");
  return [
    site.overallST,
    site.zoneST,
    site.gestureST,
    site.weaponST,
    site.fightST,
    site.passedOutST,
    site.abusiveST,
    models
  ].join("|");
}

function sensitivityFromSite(site: SiteModel): Record<SensitivityKey, number> {
  return {
    overallST: Number(site.overallST) || 5,
    zoneST: Number(site.zoneST) || 5,
    gestureST: Number(site.gestureST) || 5,
    weaponST: Number(site.weaponST) || 5,
    fightST: Number(site.fightST) || 5,
    passedOutST: Number(site.passedOutST) || 5,
    abusiveST: Number(site.abusiveST) || 5
  };
}

function modelsFromSite(site: SiteModel): ModelTypes[] {
  return filterModelsForUI(site.enabledModels as ModelTypes[]);
}

function allSitesMatch(sites: SiteModel[]) {
  if (!sites.length) return false;
  const first = settingsFingerprint(sites[0]);
  return sites.every((site) => settingsFingerprint(site) === first);
}

interface BulkManageSitesProps {
  sites: SiteModel[];
  onSuccess: () => void;
  onClose: () => void;
}

export default function BulkManageSites({ sites, onSuccess, onClose }: BulkManageSitesProps) {
  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sensitivity, setSensitivity] = useState(() =>
    allSitesMatch(sites) ? sensitivityFromSite(sites[0]) : defaultSensitivity
  );
  const [enabledModels, setEnabledModels] = useState<ModelTypes[]>(() =>
    allSitesMatch(sites) ? modelsFromSite(sites[0]) : []
  );
  const { isLoading, response, error, callFetch } = useFetch<{
    updated: string[];
    failed: { id: string; message: string }[];
  }>("PUT");

  const mixed = useMemo(() => sites.length >= 2 && !allSitesMatch(sites), [sites]);

  const onRequestSave = useCallback(() => {
    if (!enabledModels.length) {
      enqueueSnackbar(
        t["At least one detection feature is required."] || "At least one detection feature is required.",
        { variant: "error" }
      );
      return;
    }
    setConfirmOpen(true);
  }, [enabledModels.length, enqueueSnackbar, t]);

  const onConfirmSave = useCallback(() => {
    callFetch(ENDPOINTS.sites + "/bulk", {
      body: JSON.stringify({
        ids: sites.map((s) => s._id),
        ...Object.fromEntries(Object.entries(sensitivity).map(([k, v]) => [k, String(v)])),
        enabledModels
      })
    }).catch(console.error);
  }, [callFetch, sites, sensitivity, enabledModels]);

  useEffect(() => {
    if (response) {
      const failed = response.data?.failed || [];
      enqueueSnackbar(response.message || "", { variant: failed.length ? "warning" : "success" });
      if (failed.length) {
        const names = failed
          .map((f) => sites.find((s) => s._id === f.id)?.name || f.id)
          .join(", ");
        enqueueSnackbar(`Failed: ${names}`, { variant: "error" });
      }
      setConfirmOpen(false);
      onSuccess();
      onClose();
    }
  }, [response, enqueueSnackbar, onSuccess, onClose, sites]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  return (
    <>
      <MyModal
        title={`${t["Manage Models"] || "Manage Models"} — ${sites.length} ${
          sites.length === 1 ? t["Site"] || "Site" : t["Sites"] || "Sites"
        }`}
        subtitle={
          t["Configure detection sensitivity and features for the selected sites."] ||
          "Configure detection sensitivity and features for the selected sites."
        }
        open={true}
        setOpen={onClose}
        variant="light"
        width={560}
      >
        {mixed ? (
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            {t["Selected sites currently use different settings. Saving will override all of them with the values below."] ||
              "Selected sites currently use different settings. Saving will override all of them with the values below."}
          </Alert>
        ) : null}

        <FormSection title={t["Detection Sensitivity"]} description={t["Detection Sensitivity description"]}>
          <Stack spacing={1.5}>
            {sensitivityFields.map(({ key, labelKey }) => (
              <NumberStepper
                key={key}
                label={t[labelKey]}
                value={sensitivity[key]}
                min={1}
                max={10}
                onChange={(value) => setSensitivity((prev) => ({ ...prev, [key]: value }))}
              />
            ))}
          </Stack>
        </FormSection>

        <Box mt={2.5}>
          <FormSection title={t["Detection Features"]} description={t["Detection Features description"]}>
            <Stack divider={<Divider sx={{ borderColor: COLORS.surface.border }} />}>
              {UI_MODELS.map((model) => (
                <FormRowSwitch
                  key={model}
                  label={t[MODEL_LABELS[MODELS.indexOf(model)]]}
                  checked={enabledModels.includes(model)}
                  onChange={(checked) => {
                    setEnabledModels((prev) =>
                      checked ? [...prev, model] : prev.filter((item) => item !== model)
                    );
                  }}
                />
              ))}
            </Stack>
          </FormSection>
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            sx={{
              minWidth: 120,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              borderColor: COLORS.surface.border,
              color: COLORS.surface.primaryText,
              fontWeight: 500,
              textTransform: "none"
            }}
          >
            {t["Cancel"]}
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={isLoading || !enabledModels.length}
            onClick={onRequestSave}
            sx={{
              minWidth: 140,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              fontWeight: 500,
              textTransform: "none",
              ...primaryContainedButtonSx
            }}
          >
            {t["Save Changes"] || "Save Changes"}
          </Button>
        </Box>
      </MyModal>

      <BulkOverrideConfirm
        open={confirmOpen}
        count={sites.length}
        entityLabel={sites.length === 1 ? t["Site"] || "site" : t["Sites"] || "sites"}
        mixed={mixed}
        isLoading={isLoading}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onConfirmSave}
      />
    </>
  );
}
